import { CheckIcon } from '@velocity/icons/system';
import { Headline, Tabs, Text } from '@velocity/ui';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../redux/types';
import { ContactPurpose, Signer } from '../../../../types/instant-lease-api';
import { getCoApplicant } from '../../../../utils/getter';
import { updateB2CCoSigner } from '../../../../utils/helper';
import Navigation from '../../../../utils/navigation';
import {
  formatBankDetails,
  formatGermanDateToUTC,
} from '../../../../utils/sanitize-data';
import { validateSignerData } from '../../../../utils/validatetor';
import BankTemplate, {
  BankTemplateData,
} from '../../templates/bank-template/bank-template';
import ContactTemplate, {
  ContactTemplateData,
} from '../../templates/contact-template/contact-template';
import DocumentTemplate, {
  DocumentDataSubmittedValues,
} from '../../templates/document-template/document-template';
import IncomeTemplate, {
  IncomeTemplateData,
} from '../../templates/income-template/income-template';
import PersonalTemplate, {
  PersonalData,
} from '../../templates/personal-template/personal-template';
import WorkTemplate, {
  WorkTemplateData,
} from '../../templates/work-template/work-template';

const TABS = [
  {
    id: 'personalData',
    title: 'Personendaten',
    heading: 'Persönliche Angaben',
  },
  { id: 'contactData', title: 'Kontaktdaten', heading: 'Kontaktdaten' },
  {
    id: 'workData',
    title: 'Arbeit, Familie & Wohnen',
    heading: 'Arbeit, Familie & Wohnen',
  },
  { id: 'incomeData', title: 'Ein- & Ausgaben', heading: 'Ein- & Ausgaben' },
  { id: 'bankData', title: 'Bank', heading: 'Bankverbindung' },
  { id: 'documentData', title: 'Dokumentupload', heading: 'Dokumentupload' },
] as const;

type TabId = (typeof TABS)[number]['id'];

interface Props {
  hasCoApplicant?: boolean;
}

const CoApplicant: React.FC<Props> = ({ hasCoApplicant }) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Get CoApplicant Data
  const coApplicant = getCoApplicant(
    activeApplication?.customer?.private?.signers,
  );

  // Histroy Hook
  const navigate = useNavigate();

  // Navigation
  const { nextRoute, previousRoute } = new Navigation();
  const goBack = () => navigate(previousRoute());

  // Active Tab Control
  const [activeTab, setActiveTab] = useState<TabId>('personalData');

  // Tabs the user has already saved
  const [completedTabs, setCompletedTabs] = useState<TabId[]>([]);
  const isDone = (id: TabId) => completedTabs.includes(id);

  // Loading State
  const [loading, setLoading] = useState<boolean>(false);

  // Set Signer internally
  const [signerData, setSignerData] = useState<Signer | undefined>(coApplicant);

  const tabsRef = useRef<HTMLDivElement>(null);

  // Marks a tab as saved and moves on to the next one
  const completeTab = (id: TabId, next: TabId) => {
    setCompletedTabs((tabs) => (tabs.includes(id) ? tabs : [...tabs, id]));
    setActiveTab(next);
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Save Personal Data
  const submitPersonalData = async (modifiedPersonalData: PersonalData) => {
    setLoading(true);
    const updatedSigner = {
      ...signerData,
      person: {
        ...signerData?.person,
        uuid: modifiedPersonalData.uuid,
        gender: modifiedPersonalData?.gender,
        first_name: modifiedPersonalData?.first_name,
        last_name: modifiedPersonalData?.last_name,
        birth_place: modifiedPersonalData?.birth_place,
        citizenship: modifiedPersonalData?.citizenship,
        date_of_birth: modifiedPersonalData?.date_of_birth,
        contact_details: {
          ...signerData?.person?.contact_details,
          addresses: [
            {
              postal_code: modifiedPersonalData?.postal_code,
              street_line: modifiedPersonalData?.street_line,
              house_number: modifiedPersonalData?.house_number,
              locality: modifiedPersonalData.locality,
              additional_lines: [''],
            },
          ],
        },
      },
      type: modifiedPersonalData.type,
    };
    setSignerData(updatedSigner);
    updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
      () => {
        setLoading(false);
        setSignerData(updatedSigner);
        completeTab('personalData', 'contactData');
      },
    );
  };

  // Save contact Data
  const submitContactData = async (
    modifiedContactData: ContactTemplateData,
  ) => {
    setLoading(true);
    const updatedSigner = {
      ...signerData,
      person: {
        ...signerData?.person,
        contact_details: {
          ...signerData?.person?.contact_details,
          emails: [
            {
              value: modifiedContactData?.email,
              purpose: ContactPurpose.CONTACT,
            },
          ],
          phone_numbers: [
            {
              value: modifiedContactData?.phone_number,
              purpose: ContactPurpose.CONTACT,
            },
          ],
        },
      },
    };

    setSignerData(updatedSigner);
    updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
      () => {
        setLoading(false);
        completeTab('contactData', 'workData');
      },
    );
  };

  // Save contact Data
  const submitWorkData = async (modifiedWorkData: WorkTemplateData) => {
    setLoading(true);
    const updatedSigner = {
      ...signerData,
      person: {
        ...signerData?.person,
      },
      living_details: {
        ...signerData?.living_details,
        employment_type: modifiedWorkData.employment_type,
        employment_since: formatGermanDateToUTC(
          modifiedWorkData.employment_since as string,
        ),
        marital_status: modifiedWorkData.marital_status,
        number_of_children: modifiedWorkData.number_of_children,
        residence_type: modifiedWorkData.residence_type,
        residence_since: formatGermanDateToUTC(
          modifiedWorkData.residence_since as string,
        ),
      },
    };
    setSignerData(updatedSigner);
    updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
      () => {
        setLoading(false);
        completeTab('workData', 'incomeData');
      },
    );
  };

  const submitIncomeData = async (modifiedIncomeData: IncomeTemplateData) => {
    setLoading(true);
    const updatedSigner = {
      ...signerData,
      income_and_cost_details: {
        ...signerData?.income_and_cost_details,
        income: {
          currency_code: 'EUR',
          units: modifiedIncomeData.income,
          nanos: '',
        },
        additional_income: {
          currency_code: 'EUR',
          units: modifiedIncomeData.additional_income,
          nanos: '',
        },
        housing_cost: {
          currency_code: 'EUR',
          units: modifiedIncomeData.housing_cost,
          nanos: '',
        },
        additional_cost: {
          currency_code: 'EUR',
          units: modifiedIncomeData.additional_cost,
          nanos: '',
        },
      },
    };
    setSignerData(updatedSigner);
    updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
      () => {
        setLoading(false);
        completeTab('incomeData', 'bankData');
      },
    );
  };

  // Save contact Data
  const submitBankData = async (modifiedIBankData: BankTemplateData) => {
    setLoading(true);
    const bankDetails = await formatBankDetails(modifiedIBankData.iban);
    const updatedSigner = {
      ...signerData,
      person: {
        ...signerData?.person,
      },
      bank_account: {
        ...bankDetails,
        account_owner: modifiedIBankData?.account_owner,
      },
    };

    setSignerData(updatedSigner);
    updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
      () => {
        setLoading(false);
        completeTab('bankData', 'documentData');
      },
    );
  };

  // Save contact Data
  const submitDocumentData = async (
    modifiedIdentityData: DocumentDataSubmittedValues,
    goNext?: boolean,
    document?: string,
  ) => {
    setLoading(true);
    let updatedSigner;
    if (document === 'residence') {
      // Use the latest co-applicant from the store so that data written
      // elsewhere (e.g. uploaded income documents) is not overwritten by the
      // locally cached signer.
      const currentCoApplicant = getCoApplicant(
        activeApplication?.customer?.private?.signers,
      );
      updatedSigner = {
        ...signerData,
        ...currentCoApplicant,
        person: {
          ...signerData?.person,
          ...currentCoApplicant?.person,
          identity_documents: [
            {
              document_id: modifiedIdentityData.document_id,
              type: modifiedIdentityData.identity_choice,
            },
          ],
        },
      };
      setSignerData(updatedSigner);
      setCompletedTabs((tabs) => [...tabs, 'documentData']);
      updateB2CCoSigner(activeApplication, accessToken, updatedSigner).then(
        () => {
          if (goNext) navigate(nextRoute());
        },
      );
    }
  };

  const tabs = TABS.map((tab, index) => ({
    ...tab,
    done: isDone(tab.id),
    // A tab opens once the previous one is saved (or the signer data is already valid)
    disabled:
      index > 0 &&
      !validateSignerData([coApplicant as any], activeApplication) &&
      !isDone(TABS[index - 1].id),
  }));

  const panels: Record<string, React.ReactNode> = {
    personalData: (
      <PersonalTemplate
        tabButton
        onSubmit={submitPersonalData}
        privateCustomer={coApplicant}
        loading={loading}
        singer
      />
    ),
    contactData: (
      <ContactTemplate
        tabButton
        onSubmit={submitContactData}
        privateCustomer={coApplicant}
        loading={loading}
        singer
        hasCoApplicant={hasCoApplicant}
      />
    ),
    workData: (
      <WorkTemplate
        tabButton
        onSubmit={submitWorkData}
        privateCustomer={coApplicant}
        loading={loading}
      />
    ),
    incomeData: (
      <IncomeTemplate
        tabButton
        onSubmit={submitIncomeData}
        privateCustomer={coApplicant}
        loading={loading}
      />
    ),
    bankData: (
      <BankTemplate
        tabButton
        onSubmit={submitBankData}
        privateCustomer={coApplicant}
        loading={loading}
      />
    ),
    documentData: (
      <DocumentTemplate
        coApplicant
        onSubmit={submitDocumentData}
        goBack={goBack}
        privateCustomer={coApplicant}
        loading={loading}
        setLoading={(value) => value !== undefined && setLoading(value)}
      />
    ),
  };

  return (
    <div ref={tabsRef} className='flex w-full flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <Headline variant='300' component='h2'>
          Angaben zum Mitantragssteller
        </Headline>
        <Text component='p' variant='100'>
          Diese Angaben benötigen wir für die gemeinsame Bonitätsbewertung.
        </Text>
      </div>
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value as TabId)}
      >
        <Tabs.List aria-label='Angaben zum Mitantragssteller'>
          {tabs.map(({ id, title, disabled, done }) => (
            <Tabs.Tab
              key={id}
              value={id}
              disabled={disabled}
              data-testid={`tab-item-${id}`}
            >
              {done && <CheckIcon size='s' />}{' '}
              <Text
                component='span'
                variant='200'
                className='text-(--ayvens-theme-neutral-neutral-600,#757777)'
              >
                {title}
              </Text>
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <>
          {tabs.map(({ id, heading }) => (
            <Tabs.TabPanel
              key={id}
              value={id}
              data-testid={`tab-content-${id}`}
            >
              {/* Figma: 24px from the tab underline to the heading (Tabs.List already adds 8px) */}
              <div className='flex flex-col gap-6 pt-4'>
                <Headline variant='300' component='h3'>
                  {heading}
                </Headline>
                {panels[id]}
              </div>
            </Tabs.TabPanel>
          ))}
        </>
      </Tabs>
    </div>
  );
};

export default CoApplicant;
